import moment from 'moment';
import { CandidateDetails } from '@tempmee/react-blocks';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { useCandidate } from '~/hooks/useCandidate';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import type { IJob, IUser } from '~/models';
import { toCandidateBadges, toShiftCount } from '~/utils/candidateBadges';
import {
  toCandidateCredentials,
  toCandidateFields,
  toProcedureGroups,
  toSpecialtyChips,
} from './shared/utils/candidateProfileProps';

export interface IBookedProfessionalDetailsProps {
  job: Partial<IJob> | null | undefined;
  /** The professional who held the shift — `job.offer_final_reciever`, fully loaded. */
  professional: IUser;
  /** Verified credentials for the Credentials tab. */
  authorizations?: IAuthorization[] | null;
  /**
   * Whether the shell has stacked its columns. `CandidateDetails` has no
   * container query, so the two places the design restructures rather than
   * reflows — the summary row's divider direction and the badge size — are a prop.
   */
  isCollapsed?: boolean;
}

/**
 * The profile of the professional who actually held a shift, as the terminal
 * Shift Details pages render it.
 *
 * Shared by the cancelled and the completed pages because on both of them the
 * panel is the same thing: a record of who worked (or was booked for) a shift
 * that is over. They differ in what sits *above* this — a reposted notice, a
 * topper with actions — not in the profile itself, so the profile is the part
 * that is extracted.
 *
 * It owns the two hooks as well as the markup. Every value `useCandidate`
 * returns here feeds `CandidateDetails` and nothing else, so leaving them in the
 * callers only meant both pages repeating the same destructure and the same
 * `useSelectedOffice` lookup.
 *
 * No `specialtiesAlert`: the mismatch warnings the Confirm Candidates page shows
 * in that slot ask the office to act on a shift that has already ended, which is
 * why neither terminal frame draws them.
 */
export const BookedProfessionalDetails = ({
  job,
  professional,
  authorizations,
  isCollapsed = false,
}: IBookedProfessionalDetailsProps) => {
  const office = useSelectedOffice(job?.offer_owner?.id);
  const {
    licenseNumber,
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    yearsOfExperience,
  } = useCandidate(office, professional, (job ?? null) as IJob | null);

  return (
    <CandidateDetails
      layout={isCollapsed ? 'mobile' : 'desktop'}
      shiftCount={toShiftCount(professional)}
      experience={yearsOfExperience}
      rating={professional?.average_rating ?? 0}
      badges={toCandidateBadges(professional)}
      specialties={toSpecialtyChips(professional, job)}
      credentials={toCandidateCredentials(authorizations)}
      fields={toCandidateFields({
        licenseNumber,
        issuedYear: issueDate ? moment(issueDate).format('YYYY') : undefined,
        licenseState,
        nationalProviderIdentifier,
        shouldShowNpi,
      })}
      procedures={toProcedureGroups(professional, job)}
      data-testid="candidate-details"
    />
  );
};
